export const LEVEL_HELP = 'Each domain, such as Algebra, has a progression from Level 1 to Level 7. Higher levels build on earlier skills and involve more complex questions. A student can be at different levels in different domains. Placement sets their starting point, and skill checks help them move forward.\n\nA concept does not appear at every level. It often appears at only two or three levels across the whole domain, with a different kind of challenge each time. The preview buttons show the levels available to preview for that skill.\n\n“Mix the levels” includes different levels of your chosen skills. “Choose the levels” lets you pick an available level for each skill. Whole-subject practice uses each student’s saved level in each domain.';

// Plain-language summaries of the learning progression documented in bandGuides.
// These describe the domain stage, not every skill tested by a single question.
const DOMAIN_LEVELS = {
  algebra: [
    'solve simple equations and turn short word problems into equations.',
    'connect equations with graphs and tables, read function notation, and find where lines meet or cross an axis.',
    'write equations and inequalities for everyday situations, and explain what a slope or number in an equation means.',
    'build an equation from a point and slope, work with perpendicular lines, and interpret parts of an equation.',
    'solve pairs of equations and use equations or inequalities to model totals, rates, and budgets.',
    'work out whether two equations have one solution, no solution, or infinitely many, and find missing values that make this happen.',
    'build equations from detailed stories or tables, find missing numbers in equations, and work out when two equations describe the same line.',
  ],
  advanced: [
    'combine like terms and read where a curve crosses the vertical axis.',
    'expand or simplify expressions, factor out a shared factor, and read features of curved graphs.',
    'rearrange formulas and solve equations involving absolute values, roots, or factored quadratics.',
    'factor and solve quadratic equations, check possible solutions, and connect equations with curved graphs.',
    'work with more involved factoring, find where a line and a parabola meet, and read graph features from equations.',
    'build quadratic and exponential models, move or stretch graphs, and explain what key points mean in context.',
    'combine exponent and root rules, rearrange complex formulas, and choose forms of equations that reveal key graph features or the number of solutions.',
  ],
  psda: [
    'find a percentage of an amount, use a simple unit rate, and find the middle value in an ordered list.',
    'read values from graphs, calculate averages, and use unit rates and basic conversions.',
    'connect percentages with parts and totals, and find probabilities from groups or tables.',
    'use proportions, compare averages, make predictions from scatterplots, and interpret estimates from samples.',
    'calculate percentage changes, work with rates in combined units, and find missing values in averages or probabilities.',
    'combine several percentage or rate calculations, use frequency tables, and find probabilities within a selected group.',
    'explain how changing data affects its average, middle value, or range, and decide which population a sample can represent.',
  ],
  geotrig: [
    'find the area of a rectangle and use a formula for the volume of a rectangular prism.',
    'work with perimeter and use angle relationships when a line crosses parallel lines.',
    'use triangle and intersecting-line angles, find missing dimensions from area, and find a right triangle’s longest side.',
    'compare similar or matching shapes, use scale factors, and find missing sides or angles in triangles.',
    'read a circle’s center and radius from its equation, convert degrees and radians, and solve multi-step area problems.',
    'use trigonometry, similar triangles, and linked angle relationships to find missing measurements.',
    'work with circle arcs and sectors, uncover similar triangles inside larger diagrams, and work backward from the volume of a curved solid.',
  ],
  expression: [
    'recognize how ideas connect and identify what a writing task asks them to do.',
    'choose transitions for examples or time order in straightforward passages.',
    'choose transitions that show contrast and use notes to compare ideas.',
    'connect causes and effects, and use notes to explain a feature or concept.',
    'move between general ideas and specific details, and use more complex notes to explain or compare.',
    'choose what to emphasize and write concise summaries.',
    'handle exceptions and opposing points, and draw a broader conclusion from detailed notes.',
  ],
  info: [
    'identify an argument’s main position and restate information from a passage.',
    'choose a quotation that supports a claim about a literary passage.',
    'connect scientific claims with evidence and recognize stated or implied details.',
    'identify central ideas and the details that support them.',
    'draw conclusions from a passage and follow central ideas in denser writing.',
    'interpret literary evidence and make inferences from more complex passages.',
    'decide whether evidence supports or weakens a claim and draw conclusions from complex information.',
  ],
  craft: [
    'use the full sentence and passage topic to work out a word’s meaning.',
    'predict a word’s meaning and explain what a sentence contributes to a passage.',
    'interpret words in literary passages and identify a text’s main purpose.',
    'explain how a text is organized and interpret language and purpose in more demanding passages.',
    'compare paired texts and recognize a purpose that is suggested rather than stated.',
    'interpret challenging vocabulary and explain connections between paired texts.',
    'recognize subtle connections, precise word meanings, and complex text structures.',
  ],
  conventions: [
    'identify parts of speech and recognize a complete subject and verb.',
    'spot incomplete sentences and choose verb forms that fit the subject.',
    'use verb tense, apostrophes, and clear pronoun references.',
    'join sentences correctly and punctuate extra information within a sentence.',
    'punctuate connecting words and extra information, and keep verb tense consistent.',
    'use colons and dashes, avoid unnecessary punctuation, and choose suitable relative pronouns.',
    'match subjects and verbs across long phrases, place descriptions clearly, and keep lists and comparisons grammatically consistent.',
  ],
};

export function homeworkLevelDescription(domain, domainLabel, level) {
  const summary = DOMAIN_LEVELS[domain]?.[level - 1];
  return summary ? `The Level ${level} version of these questions fits this stage of ${domainLabel}. At this point, students ${summary}` : null;
}

// The short form for the practice-set box (2026-10-16): "Level 4: students identify…".
export function homeworkLevelSummary(domain, level) {
  const summary = DOMAIN_LEVELS[domain]?.[level - 1];
  return summary ? `Level ${level}: students ${summary}` : null;
}
